import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { moment } from 'i18n';
import { withNamespaces } from 'react-i18next';

import { Loading } from 'components/Loading';
import { getMyTradeHistory } from 'redux/actions/orders';
import { formatNumberToPlaces } from 'utils';
import { PaginationTableWrapper } from 'containers/Tables';
import { Box, Text } from 'components/Wrapped';
import { formatCrypto } from 'utils/numbers';

/**
 * Shows the authenticated user's matched trades (executions) for the
 * current trading pair, fetched from /api/TradeHistory. This is the
 * per-trade view — distinct from UserTradeHistoryTable which shows orders
 * (with nested matches) from /api/get-order-history.
 *
 * Backend response shape (MatchedOrderGenericClass):
 *   { orderId, market, trade, volume, rate, amount, serviceCharge, side, date }
 */
const MyTradeHistoryTable = ({
  isAuthenticated,
  myTradeHistoryIsLoading,
  myTradeHistory,
  getMyTradeHistory,
  currentPage,
  pageLimit,
  pagesAmount,
  extraProps,
}) => {
  useEffect(() => {
    if (isAuthenticated) {
      getMyTradeHistory();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const onPageChange = (pageIndex) => {
    getMyTradeHistory(pageIndex + 1, 25);
  };

  if (myTradeHistoryIsLoading) {
    return <Loading />;
  }

  return (
    <PaginationTableWrapper
      data={myTradeHistory}
      headOpacity="1"
      resizable={false}
      columns={[
        {
          Header: 'Пара',
          id: 'symbol',
          accessor: (row) => `${row.trade || ''}/${row.market || ''}`,
          Cell: ({ value }) => (
            <Text size="12px" weight={600}>
              {value}
            </Text>
          ),
        },
        {
          Header: 'Дата',
          accessor: 'date',
          Cell: ({ value }) => {
            if (!value) return null;
            const m = moment(value);
            if (!m.isValid()) return null;
            return (
              <Box pad="none">
                <Text color="#B4B6B7" size="12px" weight={600}>
                  {m.format('DD.MM.YYYY')}
                </Text>
                <Text color="#B4B6B7" size="12px" weight={600}>
                  {m.format('HH:mm:ss')}
                </Text>
              </Box>
            );
          },
        },
        {
          Header: 'Тип',
          id: 'side',
          accessor: (row) => row.side || '',
          Cell: ({ value }) => {
            const color = value && value.toUpperCase() === 'BUY' ? '#46B08E' : '#C55445';
            return (
              <Text color={color} size="12px" weight={600}>
                {value}
              </Text>
            );
          },
        },
        {
          Header: 'Цена',
          accessor: 'rate',
          Cell: ({ value }) => (
            <Text size="12px" weight={600}>
              {formatCrypto(value || 0, true)}
            </Text>
          ),
        },
        {
          Header: 'Объем',
          id: 'volume',
          accessor: (row) => ({ volume: row.volume || 0, trade: row.trade || '' }),
          Cell: ({ value: { volume, trade } }) => (
            <Text size="12px" weight={600}>
              {formatCrypto(volume, true)} {trade}
            </Text>
          ),
        },
        {
          Header: 'Сумма',
          id: 'amount',
          accessor: (row) => ({ amount: row.amount || 0, market: row.market || '' }),
          Cell: ({ value: { amount, market } }) => (
            <Text size="12px" weight={600}>
              {formatNumberToPlaces(amount)} {market}
            </Text>
          ),
        },
        {
          Header: 'Комиссия',
          id: 'fee',
          accessor: (row) => {
            const currency = row.side && row.side.toUpperCase() === 'BUY' ? row.trade : row.market;
            return { fee: row.serviceCharge || 0, currency: currency || '' };
          },
          Cell: ({ value: { fee, currency } }) => (
            <Text size="12px" weight={600}>
              {formatNumberToPlaces(fee)} {currency}
            </Text>
          ),
        },
        {
          Header: 'Номер ордера',
          accessor: 'orderId',
          Cell: ({ value }) => {
            if (!value && value !== 0) return '';
            const s = value.toString();
            return (
              <Text size="12px" weight={600}>
                {s.length > 6 ? `${s.slice(0, 3)}...${s.slice(-3)}` : s}
              </Text>
            );
          },
        },
      ]}
      style={{ height: '100%' }}
      sortable={false}
      pageSize={pageLimit}
      pages={pagesAmount}
      page={currentPage - 1}
      onPageChange={onPageChange}
      {...extraProps}
    />
  );
};

const mapStateToProps = ({ orders, auth }) => ({
  isAuthenticated: auth.isAuthenticated,
  myTradeHistoryIsLoading: orders.myTradeHistoryIsLoading,
  myTradeHistory: orders.myTradeHistory,
  currentPage: orders.myTradeHistoryPagination.currentPage,
  pageLimit: orders.myTradeHistoryPagination.limit,
  pagesAmount: orders.myTradeHistoryPagination.pagesAmount,
});

const mapDispatchToProps = {
  getMyTradeHistory,
};

export default withNamespaces()(connect(mapStateToProps, mapDispatchToProps)(MyTradeHistoryTable));
